import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useEmployeeAuth } from './EmployeePortalLayout';
import {
  User,
  Briefcase,
  Users,
  Star,
  Calendar,
  Award,
  TrendingUp,
  ChevronRight,
  MapPin,
  Clock
} from 'lucide-react';

export const EmployeeDashboard = () => {
  const { employee, api } = useEmployeeAuth();
  const [customerSatisfaction, setCustomerSatisfaction] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCustomerSatisfaction();
  }, []);

  const fetchCustomerSatisfaction = async () => {
    try {
      const response = await api.get('/api/employee-portal/customer-satisfaction');
      setCustomerSatisfaction(response.data);
    } catch (error) {
      console.error('Error fetching satisfaction:', error);
    } finally {
      setLoading(false);
    }
  };

  const getGradeColor = (grade) => {
    if (!grade) return 'bg-gray-100 text-gray-600';
    if (grade.startsWith('A')) return 'bg-green-100 text-green-700';
    if (grade.startsWith('B')) return 'bg-blue-100 text-blue-700';
    if (grade.startsWith('C')) return 'bg-yellow-100 text-yellow-700';
    return 'bg-orange-100 text-orange-700';
  };

  return (
    <div className="p-4 space-y-4">
      {/* Welcome Card */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-br from-[#1a1a1a] to-[#2d2d2d] text-white p-6 rounded-2xl"
      >
        <div className="flex items-center gap-4">
          {employee.profile_picture_url ? (
            <img 
              src={employee.profile_picture_url} 
              alt={employee.name}
              className="w-16 h-16 rounded-full object-cover border-2 border-[#C6A87C]"
            />
          ) : (
            <div className="w-16 h-16 rounded-full bg-[#C6A87C] flex items-center justify-center">
              <User className="w-8 h-8 text-white" />
            </div>
          )}
          <div className="flex-1">
            <h1 className="text-xl font-semibold">Hello, {employee.name.split(' ')[0]}!</h1>
            <p className="text-gray-400 text-sm">{employee.role}</p>
            <div className="flex items-center gap-2 mt-2">
              <span className={`px-2 py-1 text-xs font-bold rounded ${getGradeColor(employee.letter_grade)}`}>
                Grade: {employee.letter_grade || 'N/A'}
              </span>
              <span className="text-[#C6A87C] text-xs">
                {employee.years_experience} yrs experience
              </span>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Current Job Card */}
      {employee.current_job && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-2xl p-4 shadow-sm"
        >
          <div className="flex items-center gap-2 mb-3">
            <Briefcase className="w-5 h-5 text-[#C6A87C]" />
            <h2 className="font-semibold">Current Job</h2>
          </div>
          <div className="bg-[#C6A87C]/10 rounded-xl p-4">
            <p className="font-medium">{employee.current_job.project_type}</p>
            <p className="text-sm text-gray-600">{employee.current_job.client_name}</p>
            <div className="flex items-center gap-1 text-gray-500 text-sm mt-2">
              <MapPin className="w-4 h-4" />
              {employee.current_job.address}
            </div>
            <span className={`inline-block mt-2 px-2 py-1 text-xs rounded ${
              employee.current_job.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
            }`}>
              {employee.current_job.status}
            </span>
          </div>
        </motion.div>
      )}

      {/* Team Members */}
      {employee.team_members && employee.team_members.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="bg-white rounded-2xl p-4 shadow-sm"
        >
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Users className="w-5 h-5 text-[#C6A87C]" />
              <h2 className="font-semibold">Your Team</h2>
            </div>
            <span className="text-sm text-gray-500">{employee.team_members.length} members</span>
          </div>
          <div className="flex -space-x-3">
            {employee.team_members.slice(0, 5).map((member, i) => (
              <div
                key={member.id}
                className="w-10 h-10 rounded-full bg-gray-200 border-2 border-white flex items-center justify-center text-sm font-medium"
                title={member.name}
              >
                {member.profile_picture_url ? (
                  <img src={member.profile_picture_url} alt={member.name} className="w-full h-full rounded-full object-cover" />
                ) : (
                  member.name.charAt(0)
                )}
              </div>
            ))}
            {employee.team_members.length > 5 && (
              <div className="w-10 h-10 rounded-full bg-[#C6A87C] border-2 border-white flex items-center justify-center text-white text-sm font-medium">
                +{employee.team_members.length - 5}
              </div>
            )}
          </div>
        </motion.div>
      )}

      {/* Quick Stats */}
      <div className="grid grid-cols-2 gap-3">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-2xl p-4 shadow-sm"
        >
          <Award className="w-6 h-6 text-[#C6A87C] mb-2" />
          <p className="text-2xl font-bold">{employee.certifications?.length || 0}</p>
          <p className="text-sm text-gray-500">Certifications</p>
        </motion.div>
        
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          className="bg-white rounded-2xl p-4 shadow-sm"
        >
          <TrendingUp className="w-6 h-6 text-green-500 mb-2" />
          <p className="text-2xl font-bold">{employee.overall_grade?.toFixed(0) || 'N/A'}</p>
          <p className="text-sm text-gray-500">Performance Score</p>
        </motion.div>
      </div>

      {/* Customer Satisfaction (Visible to All) */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="bg-white rounded-2xl p-4 shadow-sm"
      >
        <div className="flex items-center gap-2 mb-4">
          <Star className="w-5 h-5 text-[#C6A87C]" />
          <h2 className="font-semibold">Customer Satisfaction</h2>
        </div>
        
        {loading ? (
          <div className="flex justify-center py-4">
            <div className="w-6 h-6 border-2 border-[#C6A87C] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : customerSatisfaction ? (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-1">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star
                      key={star}
                      className={`w-5 h-5 ${
                        star <= Math.round(customerSatisfaction.average_rating)
                          ? 'fill-[#C6A87C] text-[#C6A87C]'
                          : 'text-gray-300'
                      }`}
                    />
                  ))}
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  {customerSatisfaction.average_rating} avg from {customerSatisfaction.total_reviews} reviews
                </p>
              </div>
              <div className="text-right">
                <p className="text-3xl font-bold text-green-600">
                  {customerSatisfaction.would_recommend_percentage}%
                </p>
                <p className="text-xs text-gray-500">would recommend</p>
              </div>
            </div>
          </div>
        ) : (
          <p className="text-gray-500 text-sm">No reviews yet</p>
        )}
      </motion.div>

      {/* Quick Links */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.35 }}
        className="space-y-2"
      >
        {[
          { to: '/employee/schedule', icon: Calendar, label: 'View Schedule', desc: 'Check your upcoming work' },
          { to: '/employee/pay', icon: '$', label: 'My Pay & Earnings', desc: 'View pay tier and potential' },
          { to: '/employee/training', icon: Award, label: 'Training & Certs', desc: 'Level up your skills' },
        ].map((link) => (
          <Link
            key={link.to}
            to={link.to}
            className="flex items-center gap-4 bg-white rounded-2xl p-4 shadow-sm hover:shadow-md transition-shadow"
          >
            <div className="w-12 h-12 rounded-full bg-[#C6A87C]/10 flex items-center justify-center">
              {typeof link.icon === 'string' ? (
                <span className="text-xl font-bold text-[#C6A87C]">{link.icon}</span>
              ) : (
                <link.icon className="w-6 h-6 text-[#C6A87C]" />
              )}
            </div>
            <div className="flex-1">
              <p className="font-medium">{link.label}</p>
              <p className="text-sm text-gray-500">{link.desc}</p>
            </div>
            <ChevronRight className="w-5 h-5 text-gray-400" />
          </Link>
        ))}
      </motion.div>

      {/* Expertise Tags */}
      {employee.expertise && employee.expertise.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-2xl p-4 shadow-sm"
        >
          <h3 className="font-semibold mb-3">Your Expertise</h3>
          <div className="flex flex-wrap gap-2">
            {employee.expertise.map((skill) => (
              <span
                key={skill}
                className="px-3 py-1 bg-[#C6A87C]/10 text-[#C6A87C] text-sm rounded-full"
              >
                {skill}
              </span>
            ))}
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default EmployeeDashboard;
